import { useState, useRef, useEffect } from 'react';

import { CheckboxGroup } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import { isOptionDisabledDueToMutualExclusivity } from '@backrs_ui/grow-ui/src/pages/Survey/survey.util';

import { Box, SxProps, Theme, RadioGroup, Radio, FormControlLabel, Snackbar } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { QuestionText } from '../atoms/QuestionText';
import { SchoolLevelQuestionText } from '../atoms/SchoolLevelQuestionText';
import { SurveyCard } from '../atoms/SurveyCard';

export type QuestionProps = Readonly<{
  question: CareerQuestion;
  responsesForQuestion?: CareerUserResponseAnswer;
  userResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  handleFactClick?: () => void;
  isSchoolLevelQuestion?: boolean;
  actionButton?: React.ReactNode;
  isSkippable?: boolean;
  cardWidth?: string;
}>;

export function Question({
  question,
  responsesForQuestion,
  userResponseSet,
  careerQuestions,
  addUserQuestionResponse,
  handleFactClick,
  isSchoolLevelQuestion = false,
  actionButton,
  isSkippable = false,
  cardWidth = '700px',
}: QuestionProps) {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const snackbarTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (snackbarTimeoutRef.current) {
        window.clearTimeout(snackbarTimeoutRef.current);
      }
    };
  }, []);

  if (!question) {
    return null;
  }

  const { title, question: question_body, is_multi_select } = question;
  const options = question.options.filter((option) => option.is_active !== false);

  function handleAddingAnswerToResponse(optionId: number) {
    if (responsesForQuestion) {
      // if the question is multi-select, we need to toggle the option instead of just adding it
      const updatedOptionIds = is_multi_select
        ? responsesForQuestion.career_question_option_ids.includes(optionId)
          ? responsesForQuestion.career_question_option_ids.filter((id) => id !== optionId)
          : [...responsesForQuestion.career_question_option_ids, optionId]
        : [optionId];

      const updatedResponse = {
        ...responsesForQuestion,
        career_question_option_ids: updatedOptionIds,
      };

      addUserQuestionResponse(updatedResponse);

      return;
    }

    const newResponse: CareerUserResponseAnswer = {
      id: uuidv4(),
      career_user_response_set_id: userResponseSet.id,
      career_question_id: question.id,
      career_question_option_ids: [optionId],
    };

    addUserQuestionResponse(newResponse);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionId = parseInt(event.target.value);

    handleAddingAnswerToResponse(selectedOptionId);
  };

  const hasFacts = (question.facts && question.facts.length > 0) || false;

  const flashSnackbar = () => {
    setShowSnackbar(true);

    if (snackbarTimeoutRef.current) {
      window.clearTimeout(snackbarTimeoutRef.current);
    }

    snackbarTimeoutRef.current = window.setTimeout(() => {
      setShowSnackbar(false);
      snackbarTimeoutRef.current = null;
    }, 6000);
  };

  return (
    <SurveyCard width={cardWidth}>
      <Box sx={relativeSx}>
        <Snackbar
          open={showSnackbar}
          onClose={() => setShowSnackbar(false)}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          message={appStrings.modules.survey.popupMessage}
        />

        {isSchoolLevelQuestion ? (
          <SchoolLevelQuestionText title={title} question_body={question_body} />
        ) : (
          <QuestionText
            hasFacts={hasFacts}
            handleFactClick={handleFactClick}
            title={title}
            question_body={
              isSkippable
                ? `${question_body} ${appStrings.modules.survey.clickNextSkippableQuestion}`
                : question_body
            }
          />
        )}

        <Box sx={optionContainerSx}>
          {question.is_multi_select ? (
            <CheckboxGroup
              key={question.id}
              containerSx={checkboxGroupContainerSx(options.length > 4)}
              options={options.map((option) => ({
                label: option.answer,
                value: option.id.toString(),
                disabled: isOptionDisabledDueToMutualExclusivity(
                  option.answer,
                  question.calculator_key,
                  userResponseSet,
                  careerQuestions,
                ),
              }))}
              selectedValues={
                responsesForQuestion?.career_question_option_ids.map((id) => id.toString()) || []
              }
              maxSelectedOptions={
                question.calculator_key === 'sources_of_meaning' ? 4 : options.length - 1
              }
              setSelectedValues={(selectedValues) => {
                const optionIds = selectedValues.map((value) => parseInt(value));

                if (optionIds.length > options.length - 2) {
                  flashSnackbar();
                }

                if (responsesForQuestion) {
                  const updatedResponse = {
                    ...responsesForQuestion,
                    career_question_option_ids: optionIds,
                  };

                  addUserQuestionResponse(updatedResponse);
                } else {
                  const newResponse: CareerUserResponseAnswer = {
                    id: uuidv4(),
                    career_user_response_set_id: userResponseSet.id,
                    career_question_id: question.id,
                    career_question_option_ids: optionIds,
                  };

                  addUserQuestionResponse(newResponse);
                }
              }}
            />
          ) : (
            <RadioGroup
              value={responsesForQuestion?.career_question_option_ids[0]?.toString() || ''}
              onChange={handleChange}
              sx={isSchoolLevelQuestion ? schoolLevelRadioGroupSx : radioGroupSx}
            >
              {options.map((option) => {
                const isSelected =
                  responsesForQuestion?.career_question_option_ids.includes(option.id) ?? false;
                const isDisabled = isOptionDisabledDueToMutualExclusivity(
                  option.answer,
                  question.calculator_key,
                  userResponseSet,
                  careerQuestions,
                );

                return (
                  <FormControlLabel
                    id={`survey-option-${option.id}`}
                    key={`${option.answer}-${option.id}`}
                    value={option.id.toString()}
                    control={<Radio disabled={isDisabled} />}
                    label={option.answer}
                    checked={isSelected}
                    disabled={isDisabled}
                  />
                );
              })}
            </RadioGroup>
          )}
        </Box>
        {actionButton && <Box sx={actionButtonContainerSx}>{actionButton}</Box>}
      </Box>
    </SurveyCard>
  );
}

const checkboxGroupContainerSx =
  (shouldWrap: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: shouldWrap ? '50% 50%' : '1fr',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
      maxHeight: '300px',
      overflowY: 'auto',
      padding: backrs.spacing['spacing-xxs'],
    },
    zIndex: 999,
    position: 'relative',
  });

const relativeSx: SxProps<Theme> = {
  position: 'relative',
};

const radioGroupSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  zIndex: 999,
  position: 'relative',
};

const schoolLevelRadioGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '50% 50%',
  gap: backrs.spacing['spacing-lg'],
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
  zIndex: 999,
  position: 'relative',
});

const optionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-3xl'],
});

const actionButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-3xl'],
  paddingTop: backrs.spacing['spacing-lg'],
});
